<script>
	/**
	 * Quick Facts orientation block — renders a concept's metadata facets
	 * (When / Where / Owner / Depends on / Other) as a compact aside.
	 *
	 * Extracted from inline copies in ConceptFull.svelte and ExplorerTree.svelte
	 * (root + child variants). Per sprint-review-2026-04-25 frontend MEDIUM:
	 * adding a new orientation field used to require three edits across two
	 * files; now it's one.
	 *
	 * Renders nothing when no slot is populated — keeps DOM clean for concepts
	 * that have an empty `orientation` object or no aggregator output yet.
	 *
	 * Variants:
	 *   - default: full-bleed white card (used in ConceptFull + Explorer root)
	 *   - compact: tighter padding + subdued background (used for child sections
	 *     in Explorer lane 2 to differentiate from parent-level orientation)
	 */
	export let orientation = null;
	export let variant = 'default'; // 'default' | 'compact'

	// Slot shape (group-by-value pivot, 2026-04-26 schema):
	//   string                       — one unique value, no subject context.
	//   [{value, subjects[]}, ...]   — group-by-value: each unique fact once,
	//                                  with the atom.names that contributed it
	//                                  listed in subjects[].
	//   [{subject, value}, ...]      — LEGACY shape (one short-lived day before
	//                                  the pivot). Rendered transparently by
	//                                  pivoting at read time.
	//
	// All three forms collapse to the unified rendering shape:
	//   [{value, subjects[]}, ...]
	function normalizeSlot(v) {
		if (v == null) return [];
		if (typeof v === 'string') {
			const t = v.trim();
			return t ? [{ value: t, subjects: [] }] : [];
		}
		if (!Array.isArray(v) || v.length === 0) return [];

		// New shape: items carry `subjects` (array). Use directly.
		if (v[0] && typeof v[0] === 'object' && Array.isArray(v[0].subjects)) {
			return v
				.filter(e => e && typeof e.value === 'string' && e.value.trim())
				.map(e => ({
					value: e.value.trim(),
					subjects: (e.subjects || [])
						.filter(s => typeof s === 'string' && s.trim())
						.map(s => s.trim()),
				}));
		}

		// Legacy shape: items carry `subject` (string). Pivot to group-by-value.
		const byValue = new Map();
		for (const e of v) {
			if (!e || typeof e.value !== 'string' || !e.value.trim()) continue;
			const value = e.value.trim();
			const lc = value.toLowerCase();
			if (!byValue.has(lc)) byValue.set(lc, { value, subjects: new Set() });
			if (e.subject && typeof e.subject === 'string' && e.subject.trim()) {
				byValue.get(lc).subjects.add(e.subject.trim());
			}
		}
		return Array.from(byValue.values())
			.map(g => ({
				value: g.value,
				subjects: Array.from(g.subjects).sort((a, b) => a.localeCompare(b)),
			}))
			.sort((a, b) => a.value.localeCompare(b.value));
	}

	$: whenRows = normalizeSlot(orientation && orientation.when);
	$: whereRows = normalizeSlot(orientation && orientation.where);
	$: ownerRows = normalizeSlot(orientation && orientation.owner);
	$: dependsOn = (orientation && Array.isArray(orientation.depends_on))
		? [...orientation.depends_on].sort((a, b) => String(a).localeCompare(String(b)))
		: [];

	$: hasSlot = orientation && typeof orientation === 'object' && (
		whenRows.length > 0 ||
		whereRows.length > 0 ||
		ownerRows.length > 0 ||
		(Array.isArray(orientation.depends_on) && orientation.depends_on.length > 0) ||
		(orientation.other && Object.keys(orientation.other).length > 0)
	);
</script>

{#if hasSlot}
	{@const o = orientation}
	<aside class="ob-orientation" class:ob-compact={variant === 'compact'}>
		<div class="ob-label">Quick Facts</div>

		<div class="ob-table-wrap">
		<table class="ob-table">
			<tbody>
				{#if whenRows.length > 0}
					<tr class="ob-heading-row"><th colspan="2">When</th></tr>
					{#each whenRows as row}
						<tr>
							<td class="ob-cell-key ob-time">{row.value}</td>
							<td class="ob-cell-val ob-subjects">{row.subjects.join(', ')}</td>
						</tr>
					{/each}
				{/if}

				{#if whereRows.length > 0}
					<tr class="ob-heading-row"><th colspan="2">Where</th></tr>
					{#each whereRows as row}
						<tr>
							<td class="ob-cell-key">{row.value}</td>
							<td class="ob-cell-val ob-subjects">{row.subjects.join(', ')}</td>
						</tr>
					{/each}
				{/if}

				{#if dependsOn.length > 0}
					<tr class="ob-heading-row"><th colspan="2">Depends on</th></tr>
					<tr>
						<td colspan="2" class="ob-cell-pills">
							{#each dependsOn as d}<span class="ob-pill">{d}</span>{/each}
						</td>
					</tr>
				{/if}

				{#if ownerRows.length > 0}
					<tr class="ob-heading-row"><th colspan="2">Owner</th></tr>
					{#each ownerRows as row}
						<tr>
							<td class="ob-cell-key ob-owner">{row.value}</td>
							<td class="ob-cell-val ob-subjects">{row.subjects.join(', ')}</td>
						</tr>
					{/each}
				{/if}

				{#if o.other && Object.keys(o.other).length > 0}
					<tr class="ob-heading-row"><th colspan="2">More</th></tr>
					{#each Object.entries(o.other).sort((a, b) => a[0].localeCompare(b[0])) as [k, v]}
						{#if typeof v === 'string' && v.trim()}
							<tr>
								<td class="ob-cell-key">{k}</td>
								<td class="ob-cell-val">{v}</td>
							</tr>
						{/if}
					{/each}
				{/if}
			</tbody>
		</table>
		</div>
	</aside>
{/if}

<style>
	/* Quick Facts wraps the table in a light backdrop so the table reads as
	   a discrete unit even before the eye picks up the borders. The table
	   itself carries the structure — 1px medium-charcoal borders on every
	   cell, no rounding, no shadow. Reads as data, not as decoration. */
	.ob-orientation {
		margin: 0 0 24px;
		padding: 14px 18px;
		background: #ECEEF3;
		border: 0;
		border-radius: 0;
	}
	.ob-orientation.ob-compact {
		margin: 6px 0 10px;
		padding: 10px 12px;
		background: #ECEEF3;
		border-radius: 0;
	}
	.ob-label {
		font-size: 10px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: #9CA3AF;
		margin: 0 0 8px;
	}
	.ob-compact .ob-label { font-size: 9px; margin-bottom: 6px; }

	/* The table — softer borders (gray-300), lighter cell text, gray-50 cell
	   fill against the inlaid #ECEEF3 surround. The outer corners are rounded
	   by wrapping the table in .ob-table-wrap with overflow:hidden — the
	   rounding can't sit on <table> directly when border-collapse is in use
	   (the cell borders escape the radius and produce ragged corners). */
	.ob-table-wrap {
		border-radius: 6px;
		overflow: hidden;                /* clips cell edges to the radius */
		border: 1px solid #D1D5DB;       /* outer ring */
	}
	.ob-table {
		width: 100%;
		border-collapse: collapse;
		background: #F9FAFB;             /* gray-50 cell fill */
		font-size: 12px;
		color: #4B5563;                  /* softer body text */
		table-layout: auto;
	}
	.ob-compact .ob-table { font-size: 11px; }
	.ob-table th,
	.ob-table td {
		border: 1px solid #D1D5DB;       /* gray-300 — softer than the previous charcoal */
		padding: 6px 10px;
		text-align: left;
		vertical-align: top;
		line-height: 1.45;
	}
	.ob-compact .ob-table th,
	.ob-compact .ob-table td { padding: 4px 8px; }

	/* Drop the outer-edge cell borders so the wrap's outer ring is the only
	   line at the table boundary — otherwise we'd see a doubled line at the
	   rounded corners (cell border + wrap border). Interior cell borders
	   stay as the visual grid. */
	.ob-table tr:first-child th,
	.ob-table tr:first-child td { border-top: none; }
	.ob-table tr:last-child th,
	.ob-table tr:last-child td { border-bottom: none; }
	.ob-table th:first-child,
	.ob-table td:first-child { border-left: none; }
	.ob-table th:last-child,
	.ob-table td:last-child { border-right: none; }

	/* Section heading row — When / Where / Depends on / Owner / More.
	   Spans both columns; stronger weight + slightly darker fill so the
	   reader's eye groups the rows beneath it. Background sits between the
	   gray-50 cell fill and the gray-200 surround for visual hierarchy. */
	.ob-heading-row th {
		font-size: 10.5px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: #374151;                  /* softer than gray-800 */
		background: #F3F4F6;             /* gray-100 — lighter and softer */
	}
	.ob-compact .ob-heading-row th { font-size: 9.5px; }

	/* Cell role styling — kept lean. Key column slightly heavier weight,
	   value column inherits, subjects column muted further. */
	.ob-cell-key {
		font-weight: 500;
		color: #6B7280;                  /* gray-500 — softer than the previous gray-800 */
		white-space: nowrap;
		width: 1%;                        /* shrink-to-content */
	}
	.ob-cell-val {
		color: #4B5563;                  /* matches table default */
	}
	.ob-subjects {
		color: #6B7280;                  /* gray-500 — secondary value text reads softer */
	}
	.ob-time {
		font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
		font-size: 12px;
	}
	.ob-compact .ob-time { font-size: 11px; }
	.ob-owner {
		font-style: italic;
		color: #6B7280;                  /* match secondary value tone */
	}

	/* Depends-on row spans both columns and renders its items as plain
	   inline words separated by gap — no inner pill boxes. The cell border
	   is the only enclosing shape; each name reads as a tag without
	   competing visual chrome. */
	.ob-cell-pills {
		display: flex;
		flex-wrap: wrap;
		gap: 4px 12px;                   /* tighter row gap, comfortable column gap */
		font-size: 11.5px;
		line-height: 1.5;
		color: #4B5563;
	}
	.ob-pill {
		display: inline-block;
		font-size: 11.5px;
		line-height: 1.5;
		color: #4B5563;
		background: transparent;          /* no inner box */
		border: none;
		padding: 0;
	}
	.ob-compact .ob-pill { font-size: 11px; line-height: 1.4; }
</style>
